<template>
  <div class="bg-white rounded-lg border border-gray-200 overflow-hidden">
    <ComponentHeader
      title="Data Attributes"
      subtitle="Reusable data primitives, organised by role and type."
    >
      <button class="btn-secondary" :disabled="isLoading" @click="fetchAttributes">
        <font-awesome-icon :icon="['fas', 'arrows-rotate']" :spin="isLoading" />
        Refresh
      </button>
      <button class="btn-accent ml-2" @click="builderVisible = true">
        <font-awesome-icon :icon="['fas', 'plus']" />
        New Data Attribute
      </button>
    </ComponentHeader>

    <div class="flex" style="min-height: 36rem; max-height: calc(100vh - 16rem)">
      <!-- ─────────── Tree ─────────── -->
      <div class="w-80 shrink-0 border-r border-gray-100 flex flex-col min-h-0">
        <div class="px-3 py-2 border-b border-gray-100">
          <div class="relative">
            <font-awesome-icon :icon="['fas', 'magnifying-glass']" class="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 text-[10px]" />
            <input
              v-model="searchQuery"
              type="text"
              placeholder="Find an attribute…"
              class="w-full border border-gray-200 rounded px-2 py-1 pl-7 text-xs focus:outline-none focus:border-gray-400"
            />
          </div>
        </div>

        <div class="flex-1 min-h-0 overflow-y-auto py-1">
          <p v-if="isLoading && !attributes.length" class="px-3 py-3 text-xs text-gray-500">Loading…</p>
          <p v-else-if="!tree.length" class="px-3 py-3 text-xs text-gray-500">{{ attributes.length ? 'No matches.' : 'No data attributes yet.' }}</p>

          <template v-for="role in tree" :key="role.key">
            <button type="button" class="tree-row" @click="toggle(role.key)">
              <font-awesome-icon :icon="['fas', isOpen(role.key) ? 'chevron-down' : 'chevron-right']" class="tree-caret" />
              <font-awesome-icon :icon="['fas', isOpen(role.key) ? 'folder-open' : 'folder']" class="tree-folder" />
              <span class="tree-label font-semibold">{{ role.label }}</span>
              <span class="tree-count">{{ role.count }}</span>
            </button>

            <template v-if="isOpen(role.key)">
              <template v-for="type in role.types" :key="type.key">
                <button type="button" class="tree-row tree-lvl2" @click="toggle(type.key)">
                  <font-awesome-icon :icon="['fas', isOpen(type.key) ? 'chevron-down' : 'chevron-right']" class="tree-caret" />
                  <font-awesome-icon :icon="['fas', isOpen(type.key) ? 'folder-open' : 'folder']" class="tree-folder" />
                  <span class="tree-label">{{ type.label }}</span>
                  <span class="tree-count">{{ type.items.length }}</span>
                </button>

                <template v-if="isOpen(type.key)">
                  <button
                    v-for="da in type.items"
                    :key="da.id"
                    type="button"
                    class="tree-row tree-lvl3"
                    :class="{ 'is-selected': selectedId === da.id, 'opacity-60': !da.is_active }"
                    @click="selectedId = da.id"
                  >
                    <font-awesome-icon :icon="['fas', 'table']" class="tree-leaf" />
                    <span class="tree-label">{{ da.name }}</span>
                  </button>
                </template>
              </template>
            </template>
          </template>
        </div>

        <label class="px-3 py-2 border-t border-gray-100 flex items-center gap-1.5 text-[11px] text-gray-600">
          <input v-model="includeInactive" type="checkbox" />
          Show inactive
        </label>
      </div>

      <!-- ─────────── Detail ─────────── -->
      <div class="flex-1 min-w-0 overflow-y-auto">
        <div v-if="!selected" class="h-full flex items-center justify-center text-sm text-gray-400 p-10 text-center">
          Select an attribute to see its definition.
        </div>

        <div v-else class="p-6 space-y-5">
          <div>
            <div class="text-[10px] font-semibold uppercase tracking-wide text-gray-500">
              {{ roleLabel(selected) }} · {{ typeLabel(selected.attribute_type) }}
            </div>
            <div class="flex items-center gap-2 mt-1">
              <h3 class="text-base font-semibold text-gray-900">{{ selected.name }}</h3>
              <span v-if="selected.is_builtin" class="text-[10px] px-1.5 rounded bg-neutral-50 text-neutral-900">built-in</span>
              <span v-if="!selected.is_active" class="text-[10px] px-1.5 rounded bg-gray-100 text-gray-600">inactive</span>
              <span class="ml-auto inline-flex items-center gap-1.5 text-[11px]" :class="selected.verified_at ? 'text-green-dark' : 'text-gray-400'">
                <span class="w-1.5 h-1.5 rounded-full" :class="selected.verified_at ? 'bg-green-dark' : 'bg-gray-300'" />
                {{ selected.verified_at ? `verified ${formatDate(selected.verified_at)}` : 'unverified' }}
              </span>
            </div>
            <p v-if="selected.description" class="text-sm text-gray-700 mt-2">{{ selected.description }}</p>
          </div>

          <div class="grid grid-cols-3 gap-4 border border-gray-200 rounded-lg px-4 py-3">
            <div>
              <div class="text-[10px] uppercase tracking-wide text-gray-500">Category</div>
              <div class="text-sm text-gray-900">{{ (selected.category && selected.category.name) || '—' }}</div>
            </div>
            <div>
              <div class="text-[10px] uppercase tracking-wide text-gray-500">Source</div>
              <div class="text-sm text-gray-900">{{ (selected.data_source && selected.data_source.name) || 'Warehouse' }}</div>
            </div>
            <div>
              <div class="text-[10px] uppercase tracking-wide text-gray-500">Unit</div>
              <div class="text-sm text-gray-900">{{ selected.unit || '—' }}</div>
            </div>
            <div v-if="selected.date_column">
              <div class="text-[10px] uppercase tracking-wide text-gray-500">Date column</div>
              <div class="text-sm text-gray-900 font-mono">{{ selected.date_column }}</div>
            </div>
            <div v-if="selected.injection_checked_at">
              <div class="text-[10px] uppercase tracking-wide text-gray-500">Injection checked</div>
              <div class="text-sm text-gray-900">{{ formatDate(selected.injection_checked_at) }}</div>
            </div>
            <div>
              <div class="text-[10px] uppercase tracking-wide text-gray-500">Columns</div>
              <div class="text-sm text-gray-900 tabular-nums">{{ (selected.output_columns || []).length || '—' }}</div>
            </div>
          </div>

          <div v-if="(selected.output_columns || []).length">
            <div class="text-[10px] font-semibold uppercase tracking-wide text-gray-500 mb-1.5">Output columns</div>
            <div class="flex flex-wrap gap-1">
              <span v-for="c in selected.output_columns" :key="c" class="font-mono text-[11px] px-1.5 py-0.5 rounded bg-gray-50 border border-gray-200 text-gray-700">{{ c }}</span>
            </div>
          </div>

          <div v-if="selected.sql_query">
            <div class="text-[10px] font-semibold uppercase tracking-wide text-gray-500 mb-1.5">SQL</div>
            <pre class="text-[11px] leading-relaxed font-mono text-gray-100 bg-gray-900 rounded-lg p-4 overflow-auto max-h-[28rem] whitespace-pre">{{ selected.sql_query }}</pre>
          </div>
        </div>
      </div>
    </div>

    <p v-if="successMessage" class="px-6 py-2 text-xs text-green-dark border-t border-gray-100">{{ successMessage }}</p>

    <DataAttributeBuilderModal
      :visible="builderVisible"
      @close="builderVisible = false"
      @created="onCreated"
    />
  </div>
</template>

<script>
import axios from 'axios';
import ComponentHeader from '@/components/widgetcomponents/library/ComponentHeader.vue';
import DataAttributeBuilderModal from './DataAttributeBuilderModal.vue';

const ATTR_BASE = '/api/impact_analytics/attributes/';

const ROLES = [
  { key: 'population', label: 'Population' },
  { key: 'denominator', label: 'Denominator' },
  { key: 'intervention', label: 'Intervention' },
  { key: 'eagle_eye', label: 'Eagle Eye' },
];

const TYPES = {
  categorical: 'Categorical',
  continuous: 'Continuous',
  multi_categorical: 'Multi-categorical',
  date: 'Date',
};

export default {
  name: 'OverviewETLDataAttributes',

  components: { ComponentHeader, DataAttributeBuilderModal },

  data() {
    return {
      attributes: [],
      searchQuery: '',
      includeInactive: false,
      open: new Set(),
      selectedId: null,
      isLoading: false,
      builderVisible: false,
      successMessage: null,
    };
  },

  computed: {
    visible() {
      const q = this.searchQuery.trim().toLowerCase();
      return this.attributes.filter((da) => {
        if (!this.includeInactive && da.is_active === false) return false;
        if (!q) return true;
        return [da.name, da.description, (da.category && da.category.name), (da.output_columns || []).join(' ')]
          .some((f) => (f || '').toLowerCase().includes(q));
      });
    },
    tree() {
      const byRole = {};
      this.visible.forEach((da) => {
        const r = this.roleKey(da);
        const t = da.attribute_type || 'other';
        byRole[r] = byRole[r] || {};
        (byRole[r][t] = byRole[r][t] || []).push(da);
      });
      const roleOrder = [
        ...ROLES.map((r) => r.key),
        ...Object.keys(byRole).filter((k) => k !== 'unassigned' && !ROLES.some((r) => r.key === k)).sort(),
        'unassigned',
      ];
      const typeOrder = [...Object.keys(TYPES), 'other'];
      return roleOrder
        .filter((r) => byRole[r])
        .map((r) => {
          const types = typeOrder
            .filter((t) => byRole[r][t])
            .map((t) => ({
              key: `${r}/${t}`,
              label: TYPES[t] || 'Other',
              items: [...byRole[r][t]].sort((a, b) => a.name.localeCompare(b.name)),
            }));
          return {
            key: r,
            label: this.roleLabelFor(r),
            count: types.reduce((n, t) => n + t.items.length, 0),
            types,
          };
        });
    },
    selected() {
      return this.attributes.find((da) => da.id === this.selectedId) || null;
    },
  },

  watch: {
    searchQuery(q) {
      if (q.trim()) this.openAll();
    },
  },

  mounted() {
    this.fetchAttributes();
  },

  methods: {
    roleKey(da) {
      return (da.role || '').trim().toLowerCase() || 'unassigned';
    },
    roleLabelFor(key) {
      const r = ROLES.find((x) => x.key === key);
      if (r) return r.label;
      if (key === 'unassigned') return 'Unassigned';
      return String(key).replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
    },
    roleLabel(da) {
      return this.roleLabelFor(this.roleKey(da));
    },
    typeLabel(type) {
      return TYPES[type] || type || 'Other';
    },
    formatDate(iso) {
      const d = new Date(iso);
      return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString();
    },
    isOpen(key) {
      return this.open.has(key);
    },
    toggle(key) {
      const next = new Set(this.open);
      if (next.has(key)) next.delete(key); else next.add(key);
      this.open = next;
    },
    openAll() {
      const next = new Set();
      this.tree.forEach((r) => {
        next.add(r.key);
        r.types.forEach((t) => next.add(t.key));
      });
      this.open = next;
    },

    async fetchAttributes() {
      this.isLoading = true;
      try {
        const { data } = await axios.get(ATTR_BASE);
        this.attributes = Array.isArray(data) ? data : (data.results || []);
        if (!this.open.size && this.tree.length) this.open = new Set([this.tree[0].key]);
      } catch {
        this.attributes = [];
      } finally {
        this.isLoading = false;
      }
    },

    onCreated(da) {
      this.successMessage = `Data attribute “${da.name}” created.`;
      this.fetchAttributes().then(() => {
        if (da && da.id) {
          this.selectedId = da.id;
          const r = this.roleKey(da);
          this.open = new Set([...this.open, r, `${r}/${da.attribute_type || 'other'}`]);
        }
      });
    },
  },
};
</script>

<style scoped>
.tree-row {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px 4px 10px;
  text-align: left;
  font-size: 12px;
  color: #353537;
  background: transparent;
  border: 0;
  cursor: pointer;
  line-height: 1.4;
}
.tree-row:hover { background: #FAFAFA; }
.tree-lvl2 { padding-left: 24px; }
.tree-lvl3 { padding-left: 52px; }
.tree-row.is-selected { background: #FFF7F4; color: #1E1F21; }
.tree-caret { font-size: 8px; color: #A5A5A6; width: 8px; flex-shrink: 0; }
.tree-folder { font-size: 11px; color: #F1BD6C; flex-shrink: 0; }
.tree-leaf { font-size: 10px; color: #8E8F90; flex-shrink: 0; }
.tree-label { flex: 1; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.tree-count { font-size: 10px; color: #8E8F90; font-variant-numeric: tabular-nums; }
</style>
